import { PencilLine } from 'lucide-react';
import { useState } from 'react';
import { AgentHandoff } from '../../components/jobs/AgentHandoff';
import { Modal, useToast } from '../../components/ui/overlays';
import { Button, Field, Input, Select, Switch, Textarea } from '../../components/ui/primitives';
import { useContentChannels, useScheduleEdit } from '../../hooks/queries';

/**
 * Queues an EDIT of a published post: creates a batch-of-one carrying the post id and the admin's
 * instructions, plus a CONTENT job. An MCP agent then revises the post; on approval the live Ghost
 * post is updated in place.
 */
interface ScheduleEditProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  postId: string;
  postTitle: string;
  onQueued?: () => void;
}

export const ScheduleEditModal = (props: ScheduleEditProps) => props.open ? <ScheduleEditForm {...props} /> : null;

const ScheduleEditForm = ({
  open,
  onClose,
  projectId,
  postId,
  postTitle,
  onQueued,
}: ScheduleEditProps) => {
  const scheduleEdit = useScheduleEdit(projectId);
  const { data: channels } = useContentChannels(projectId);
  const toast = useToast();
  const [queued, setQueued] = useState(false);

  const [form, setForm] = useState({
    instructions: '',
    channelId: '',
    requireApproval: true,
    minSeoScore: '',
  });

  const submit = async () => {
    if (scheduleEdit.isPending) return;
    if (form.instructions.trim().length < 3) {
      toast.error('Tell the agent what to change (at least 3 characters).');
      return;
    }
    if (form.minSeoScore !== '' && (!Number.isInteger(Number(form.minSeoScore)) || Number(form.minSeoScore) < 0 || Number(form.minSeoScore) > 100)) {
      toast.error('Minimum SEO score must be a whole number from 0 to 100');
      return;
    }
    try {
      await scheduleEdit.mutateAsync({
        postId,
        instructions: form.instructions.trim(),
        channelId: form.channelId || undefined,
        requireApproval: form.requireApproval,
        minSeoScore: form.minSeoScore === '' ? undefined : Number(form.minSeoScore),
      });
      setQueued(true);
      onQueued?.();
    } catch (error) {
      toast.error(error);
    }
  };

  if (queued) return <Modal open onClose={onClose} title="Post revision ready" description="Start your connected agent to revise the post." footer={<Button onClick={onClose}>Done</Button>}><AgentHandoff projectId={projectId} type="CONTENT" summary="Post revision queued" onClose={onClose} /></Modal>;

  return (
    <Modal
      open={open}
      onClose={() => { if (!scheduleEdit.isPending) onClose(); }}
      title="Schedule edit"
      description={`An agent will revise "${postTitle}". On approval, the live post is updated in place — same URL.`}
      footer={
        <>
          <Button variant="ghost" disabled={scheduleEdit.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={scheduleEdit.isPending}
            disabled={form.instructions.trim().length < 3}
            icon={<PencilLine className="h-4 w-4" />}
          >
            Schedule edit
          </Button>
        </>
      }
    >
      <fieldset disabled={scheduleEdit.isPending} className="space-y-4">
        <Field
          label="What should the AI change?"
          htmlFor="edit-instructions"
          hint="Be specific. The agent rewrites the whole post to your instructions, then it is re-scored."
        >
          <Textarea
            id="edit-instructions"
            rows={5}
            maxLength={8000}
            className="prompt-input"
            value={form.instructions}
            placeholder="Refresh the 2025 prices to 2026. Add a section on insurance. Tighten the intro to two paragraphs."
            onChange={(event) => setForm({ ...form, instructions: event.target.value })}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Publish to" htmlFor="edit-channel" hint="Defaults to the post's channel.">
            <Select
              id="edit-channel"
              value={form.channelId}
              onChange={(event) => setForm({ ...form, channelId: event.target.value })}
            >
              <option value="">Post's channel</option>
              {(channels ?? []).map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.label}
                  {channel.isDefault ? ' · default' : ''}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Minimum SEO score"
            htmlFor="edit-seo"
            hint="Below this, the revision is held for review."
          >
            <Input
              id="edit-seo"
              type="number"
              min={0}
              max={100}
              value={form.minSeoScore}
              placeholder="Channel default"
              onChange={(event) => setForm({ ...form, minSeoScore: event.target.value })}
            />
          </Field>
        </div>

        <div className="rounded-lg border border-line bg-raised px-4 py-3">
          <Switch
            checked={form.requireApproval}
            onChange={(value) => setForm({ ...form, requireApproval: value })}
            label="Review the revision before it goes live"
            description={
              form.requireApproval
                ? 'The revision lands in Posts → Drafts and waits for you to approve it.'
                : 'The revision is scheduled automatically once it clears the SEO score.'
            }
          />
        </div>
      </fieldset>
    </Modal>
  );
};
